import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {TicketListUrl} from "../config/api.ts";
import axios from "axios";

interface Ticket {
    id: number;
    subject: string;
    description: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    organizationId: number;
    customStatusId: string;
    tags: string[];
}

interface TicketListResponse {
    reset?: boolean;
    type: "active" | "closed";
    tag: "live_perfusion" | "post_perfusion" | "general";
    data: {
        totalCount: number;
        tickets: Ticket[];
        customStatuses: [];
        organizations: [];
    };
}

interface TicketListState {
    active: {
        totalCount: number | undefined;
        pageSize: number;
        live_perfusion: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
        post_perfusion: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
        general: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
    };
    closed: {
        totalCount: number | undefined;
        pageSize: number;
        live_perfusion: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
        post_perfusion: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
        general: {
            data: Ticket[];
            total: number | undefined;
            page: number;
        };
    };
    retrigger: number;
    loading?: "active" | "closed";
    updateStatus?: boolean;
    error: string | null;
    organizations: any[];
    ticketDetails: {
        request: any;
        customStatuses: any[];
        organizations: any[];
    };
}

const initialState: TicketListState = {
    active: {
        totalCount: undefined,
        pageSize: 20,
        live_perfusion: {
            data: [],
            total: undefined,
            page: 1,
        },
        post_perfusion: {
            data: [],
            total: undefined,
            page: 1,
        },
        general: {
            data: [],
            total: undefined,
            page: 1,
        },
    },
    closed: {
        totalCount: undefined,
        pageSize: 20,
        live_perfusion: {
            data: [],
            total: undefined,
            page: 1,
        },
        post_perfusion: {
            data: [],
            total: undefined,
            page: 1,
        },
        general: {
            data: [],
            total: undefined,
            page: 1,
        },
    },
    retrigger: 0,
    loading: undefined,
    error: null,
    organizations: [],
    updateStatus: false,
    ticketDetails: {
        request: {},
        customStatuses: [],
        organizations: [],
    },
};

export const updateTicketStatus = createAsyncThunk(
    "tickets/updateStatus",
    async (
        {
            ticketId,
            payload,
        }: {
            ticketId: any;
            payload: { customTicketStatusId: number; tag: string, statusName:string };
        },
        {rejectWithValue, dispatch}
    ) => {
        try {
            let url = TicketListUrl + `/${ticketId}/status_update`;
            const response = await axios.patch(url, payload);
            if(response.status===200){
                dispatch(fetchTicketDetailsById({id:ticketId}))
            }
            if (response.status !== 200) {
                return rejectWithValue(
                    `Failed to update ticket status: ${response.status}`
                );
            }
            return {ticketId, payload};
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchTicketDetailsById = createAsyncThunk<
    {
        ticketData: {
            request: any;
            customStatuses: any[];
            organizations: any[];
        };
    },
    {
        id: string;
    }
>("tickets/fetchTicketDetailsById", async (args: any) => {
    let url = TicketListUrl + `/${args.id}`;
    const response = await axios.get(url);
    if (response.data.success) {
        return {
            ticketData: response.data.data,
        };
    } else {
        throw new Error(response.data.message || "Failed to fetch tickets");
    }
});

export const fetchTotalTickets = createAsyncThunk<
    { total: number; type: "active" | "closed" },
    {
        type: "active" | "closed";
        status?: string;
        query?: string;
    }
>("tickets/fetchTotal", async (args) => {
    let url = TicketListUrl + `?tags="${args.type}"&page=1&pageSize=1`;

    if (args.status) url += `&custom_status_id=${args.status}`;
    if (args.query){
        const encodedeSearchString = encodeURIComponent(args.query)
        url += `&query=${encodedeSearchString}`;
    } 
    const response = await axios.get(url);
    if (response.data.success) {
        return {
            total: response.data.data.totalCount,
            type: args.type,
        };
    } else {
        throw new Error(response.data.message || "Failed to fetch tickets");
    }
});

   const checkIfCategoryComplete = (category: any): boolean => {
    return category.data.length === category.total;
   };

   const getNextTag = (categoryData: any): string | undefined => {
    const { live_perfusion, post_perfusion, general } = categoryData;
    if (!checkIfCategoryComplete(live_perfusion)) return "live_perfusion";
    if (!checkIfCategoryComplete(post_perfusion)) return "post_perfusion";
    if (!checkIfCategoryComplete(general)) return "general";
    return undefined;
   };

export const fetchTickets = createAsyncThunk<
    TicketListResponse | undefined,
    {
        type: "active" | "closed";
        query?: string;
        reset?: boolean;
        status?: string;
        sortBy?: string;
        order?: string;
    }
>("tickets/fetchTickets", async (args, { getState}: any) => {
    try {
        const state = getState().tickets;
        const categoryData = state[args.type];
        let tag = args.reset ? "live_perfusion" : getNextTag(categoryData);
        if (!tag) return undefined;

        let url =
            TicketListUrl +
            `?tags="${tag} ${args.type}"&page=${
                args.reset ? 1 : state[args.type][tag].page
            }&pageSize=${state[args.type].pageSize}`;
        if (args.query){
            const encodedeSearchString = encodeURIComponent(args.query)
            url += `&query=${encodedeSearchString}`;
        } 
        if (args.status) url += `&custom_status_id=${args.status}`;
        if (args.sortBy && args.order)
            url += `&sort_by=${args.sortBy}&sort_order=${args.order}`;
        const response = await axios.get(url);
        if (response.data.success) {
            return {
                reset: args.reset,
                type: args.type,
                tag,
                data: response.data.data,
            } as TicketListResponse;
        } else {
            throw new Error(response.data.message || "Failed to fetch tickets");
        }
    } catch (error: any) {
        throw new Error(error.message || "Failed to fetch tickets");
    }
});

const ticketListSlice = createSlice({
    name: "tickets",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchTickets.pending, (state, payload) => {
                state.loading = payload.meta.arg.type;
                state.error = null;
            })
            .addCase(fetchTickets.fulfilled, (state, action) => {
                state.loading = undefined;
                if (!action.payload) return;
                if (action.payload.reset) {
                    state[action.payload.type]["live_perfusion"].data =
                        action.payload.data.tickets;
                    state[action.payload.type]["live_perfusion"].page = 2;
                    state[action.payload.type]["live_perfusion"].total =
                        action.payload.data.totalCount;

                    state[action.payload.type]["post_perfusion"].data = [];
                    state[action.payload.type]["post_perfusion"].page = 1;
                    state[action.payload.type]["post_perfusion"].total = undefined;

                    state[action.payload.type]["general"].data = [];
                    state[action.payload.type]["general"].page = 1;
                    state[action.payload.type]["general"].total = undefined;
                    if (action.payload.data.organizations)
                        state.organizations = action.payload.data.organizations;
                } else {
                    state[action.payload.type][action.payload.tag].data = [
                        ...state[action.payload.type][action.payload.tag].data,
                        ...action.payload.data.tickets,
                    ];
                    state[action.payload.type][action.payload.tag].page += 1;
                    state[action.payload.type][action.payload.tag].total =
                        action.payload.data.totalCount;
                    if (action.payload.data.organizations)
                        state.organizations = [
                            ...state.organizations,
                            ...action.payload.data.organizations,
                        ];
                }
                if (action.payload.data.tickets.length < 20) {
                    state.retrigger += 1;
                    state.loading = action.payload.type;
                }
            })
            .addCase(fetchTickets.rejected, (state, action) => {
                state.loading = undefined;
                state.error = action.error.message ?? "Failed to fetch KPI data";
            })
            .addCase(fetchTotalTickets.fulfilled, (state, action) => {
                state[action.payload.type].totalCount = action.payload.total;
            })

            .addCase(updateTicketStatus.pending, (state) => {
                state.error = null;
            })
            .addCase(
                updateTicketStatus.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        ticketId: number;
                        payload: { customTicketStatusId: number; tag: string };
                    }>
                ) => {
                    state.loading = undefined;
                    const index = state.active[action.payload.payload.tag].data.findIndex(
                        (item) => item.id === action.payload.ticketId
                    );
                    if (index > -1) {
                        state.active[action.payload.payload.tag].data[
                            index
                            ].customStatusId = action.payload.payload.customTicketStatusId;
                    }
                }
            )
            .addCase(updateTicketStatus.rejected, (state, action) => {
                state.error = action.payload as string;
            })

            .addCase(fetchTicketDetailsById.fulfilled, (state, action) => {
                state.ticketDetails.request = action.payload?.ticketData?.request;
            });
    },
});

export default ticketListSlice.reducer;
